import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { convertKmphTo } from "../../../utils/distance";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { eventIconUrl } from "./eventIcons";
import { TooltipController } from "./tooltip";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const eventLabel = (point: VehicleMapDataPoint) =>
  point.resolvedDescription ||
  point.status?.description ||
  `Status ${point.statuscode}`;

const speedUnitSuffix = (unit: SPEED_UNITS) => {
  switch (unit) {
    case SPEED_UNITS.MPH:
      return "mph";
    case SPEED_UNITS.NAUTICAL_MILES:
      return "kn";
    case SPEED_UNITS.KMH:
    default:
      return "km/h";
  }
};

const tooltipHtmlFor = (
  tripLabel: string,
  point: VehicleMapDataPoint,
  speedUnit: SPEED_UNITS
) => `
  <div><strong>${tripLabel}</strong></div>
  <div>${eventLabel(point)}</div>
  <div>${Math.round(convertKmphTo(speedUnit, point.speedkph))} ${speedUnitSuffix(speedUnit)}</div>
`;

// "1x" pacing: one reported fix at a time, deliberately slow, but a whole
// trip never takes longer than TARGET_DURATION_MS - a busy trip reports
// thousands of fixes, and 450ms each ran a single replay past ten minutes,
// most of it parked in the same spot, which read as a replay that had hung
const MAX_STEP_DELAY_MS = 450;
const TARGET_DURATION_MS = 60000;

// The clock and progress bar are React state on the page hosting the map,
// and every update redraws that page. On the Asset Map one redraw takes
// ~60ms, so reporting every step capped the replay at ~16 steps a second -
// slower than 1x, and 10x made no difference. Reporting a few times a
// second leaves the timer, not the page, setting the pace.
const PROGRESS_REPORT_INTERVAL_MS = 200;

// Steps are scheduled against the clock rather than one timer each. At 10x
// a busy trip's step is ~3ms - under what the browser's timers can reliably
// deliver - so one-timer-per-step ran well short of the speed picked. Now a
// late timer is made up by revealing the fixes that have fallen due
// together, yielding at least once a frame so the map still paints.
const FRAME_MS = 16;
// how far behind the schedule can fall before it is let go - a throttled
// background tab would otherwise come back and race through the backlog
const MAX_SCHEDULE_LAG_MS = 250;

// the whole trip starts in view, so only re-centre once the vehicle is
// right at the edge - someone zoomed in to ride along, say
const RECENTER_EDGE_MARGIN = 0.03;

export type ReplayableTrip = {
  // every reported fix - the line and the vehicle marker walk all of them
  points: VehicleMapDataPoint[];
  color: string;
  label: string;
};

// walks every fix in order, growing a line with an arrowhead on its tip,
// and drops an event pin at each fix `isPinned` lets through. The pins
// follow the Event Filter; the movement does not - an account whose filter
// starts at ignition on/off only (Hide Asset Map Pinpoints) used to get a
// two-event "replay" that was over before the map had finished moving.
// multiple trips replay in sequence and stay on the map once drawn. speed
// is read fresh each step so it can change mid-replay.
export const replayTrips = ({
  map,
  trips,
  tooltip,
  isPinned = () => true,
  getSpeedMultiplier,
  speedUnit = SPEED_UNITS.KMH,
  onProgress,
  onDone,
}: {
  map: any;
  trips: ReplayableTrip[];
  tooltip: TooltipController;
  // which fixes get an event pin - the Event Filter's current selection
  isPinned?: (point: VehicleMapDataPoint) => boolean;
  getSpeedMultiplier: () => number;
  // the account's configured speed unit, for the per-event hover tooltip
  speedUnit?: SPEED_UNITS;
  // clock is the reported time of the point just revealed - a real
  // timestamp, not interpolated, since events replay one reported fix at a
  // time
  // speedKph is the asset's reported speed at that same fix - what it was
  // actually doing at the time on the clock, which is the question the
  // timestamp alone invites
  onProgress: (percent: number, clock?: number, speedKph?: number) => void;
  onDone: () => void;
}) => {
  const google = (window as any).google;
  let stopped = false;

  // frame the whole trip up front, so the route reads as it draws and the
  // camera rarely has to move; followCamera covers anything that strays out
  const startBounds = new google.maps.LatLngBounds();
  trips.forEach((trip) =>
    trip.points.forEach((point) =>
      startBounds.extend({ lat: point.latitude, lng: point.longitude })
    )
  );
  if (!startBounds.isEmpty()) map.fitBounds(startBounds, 60);

  // pans only once the vehicle reaches the viewport's edge
  const followCamera = (position: { lat: number; lng: number }) => {
    const viewport = map.getBounds?.();
    if (!viewport) {
      map.panTo(position);
      return;
    }
    const ne = viewport.getNorthEast();
    const sw = viewport.getSouthWest();
    const latSpan = ne.lat() - sw.lat();
    const lngSpan = ne.lng() - sw.lng();
    const withinComfortZone =
      position.lat > sw.lat() + latSpan * RECENTER_EDGE_MARGIN &&
      position.lat < ne.lat() - latSpan * RECENTER_EDGE_MARGIN &&
      position.lng > sw.lng() + lngSpan * RECENTER_EDGE_MARGIN &&
      position.lng < ne.lng() - lngSpan * RECENTER_EDGE_MARGIN;
    if (!withinComfortZone) map.panTo(position);
  };

  const markers: any[] = [];
  const polylines: any[] = [];

  const totalPoints = trips.reduce((sum, trip) => sum + trip.points.length, 0);
  let pointsDoneSoFar = 0;
  let lastReportedAt = -Infinity;
  let lastReportedCount = 0;
  let lastRevealedTimestamp: number | undefined;
  let lastRevealedSpeedKph: number | undefined;
  const reportProgress = () => {
    if (lastReportedCount === pointsDoneSoFar) return;
    lastReportedAt = performance.now();
    lastReportedCount = pointsDoneSoFar;
    onProgress(
      Math.round((pointsDoneSoFar / totalPoints) * 100),
      lastRevealedTimestamp,
      lastRevealedSpeedKph
    );
  };
  const stepDelayMs = Math.min(
    MAX_STEP_DELAY_MS,
    TARGET_DURATION_MS / Math.max(totalPoints, 1)
  );

  // where the vehicle is now, and which way it's heading - the same
  // arrowhead the route's first draw rides on its tip. With a narrow Event
  // Filter most fixes drop no pin, so without it there'd be nothing to
  // follow but the line.
  const arrowHeadIcon = (color: string) => ({
    icon: {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: color,
    },
    offset: "100%",
  });

  // paused holds the loop between events rather than tearing anything down,
  // so resuming picks up exactly where it left off
  let paused = false;
  let resumeWaiters: (() => void)[] = [];
  const waitIfPaused = () => {
    if (!paused) return Promise.resolve();
    return new Promise<void>((resolve) => resumeWaiters.push(resolve));
  };

  const run = async () => {
    let dueAt = performance.now();
    let lastYieldAt = dueAt;

    for (const trip of trips) {
      if (stopped) return;
      // only the trip being driven carries the arrowhead
      polylines[polylines.length - 1]?.setOptions({ icons: [] });

      const polyline = new google.maps.Polyline({
        path: [],
        map,
        strokeColor: trip.color,
        strokeOpacity: 0.85,
        strokeWeight: 2.5,
        icons: [arrowHeadIcon(trip.color)],
      });
      polylines.push(polyline);
      // grown a fix at a time - handing setPath the whole revealed array
      // on every step copied the path again each time, which adds up to
      // millions of points over a trip of a couple of thousand fixes
      const revealedPath = polyline.getPath();

      for (let index = 0; index < trip.points.length; index++) {
        if (stopped) return;
        const point = trip.points[index];
        const position = { lat: point.latitude, lng: point.longitude };

        if (isPinned(point)) {
          const marker = new google.maps.Marker({
            position,
            map,
            icon: {
              url: eventIconUrl(point, 24),
              scaledSize: new google.maps.Size(24, 24),
              anchor: new google.maps.Point(12, 12),
            },
            zIndex: 10 + index,
          });
          const html = tooltipHtmlFor(trip.label, point, speedUnit);
          marker.addListener("mouseover", (e: any) => {
            const domEvent = e?.domEvent as MouseEvent | undefined;
            tooltip.show(html, domEvent?.clientX ?? 0, domEvent?.clientY ?? 0);
          });
          marker.addListener("mouseout", () => tooltip.hide());
          markers.push(marker);
        }

        // a parked vehicle repeats its fix; a zero-length last segment
        // leaves the arrowhead with no heading, so it would swing north
        const lastRevealed =
          revealedPath.getLength() > 0
            ? revealedPath.getAt(revealedPath.getLength() - 1)
            : null;
        if (
          !lastRevealed ||
          lastRevealed.lat() !== position.lat ||
          lastRevealed.lng() !== position.lng
        )
          revealedPath.push(new google.maps.LatLng(position.lat, position.lng));
        followCamera(position);

        pointsDoneSoFar += 1;
        lastRevealedTimestamp = point.timestamp;
        lastRevealedSpeedKph = point.speedkph;

        const isLastPointOverall =
          trip === trips[trips.length - 1] && index === trip.points.length - 1;
        if (
          isLastPointOverall ||
          performance.now() - lastReportedAt >= PROGRESS_REPORT_INTERVAL_MS
        )
          reportProgress();
        if (!isLastPointOverall) {
          dueAt += stepDelayMs / getSpeedMultiplier();
          const now = performance.now();
          dueAt = Math.max(dueAt, now - MAX_SCHEDULE_LAG_MS);
          if (dueAt > now) {
            await wait(dueAt - now);
            lastYieldAt = performance.now();
          } else if (now - lastYieldAt >= FRAME_MS) {
            // behind schedule: keep revealing, but let the map paint
            await wait(0);
            lastYieldAt = performance.now();
          }
          if (paused) {
            // a paused clock has to read the moment it stopped on
            reportProgress();
            await waitIfPaused();
            // the pause isn't time the schedule owes anything for
            dueAt = performance.now();
            lastYieldAt = dueAt;
          }
          if (stopped) return;
        }
      }
    }
    if (stopped) return;
    // the finished picture is just the route, as after the first draw
    polylines[polylines.length - 1]?.setOptions({ icons: [] });
    onDone();
  };

  // wait for the camera move and tiles above to settle, or events drop
  // over a map that's still blank. the timeout covers "idle" never firing
  // because the camera was already where we asked.
  let started = false;
  const startOnce = () => {
    if (started || stopped) return;
    started = true;
    run();
  };
  const idleListener = google.maps.event.addListenerOnce(
    map,
    "idle",
    startOnce
  );
  const startFallbackTimer = setTimeout(startOnce, 1200);

  return {
    stop: () => {
      stopped = true;
      // an indefinitely-paused run would otherwise never see `stopped`
      paused = false;
      resumeWaiters.forEach((resolve) => resolve());
      resumeWaiters = [];
      clearTimeout(startFallbackTimer);
      google.maps.event.removeListener(idleListener);
      markers.forEach((marker) => marker.setMap(null));
      polylines.forEach((polyline) => polyline.setMap(null));
    },
    pause: () => {
      paused = true;
    },
    resume: () => {
      if (!paused) return;
      paused = false;
      const waiters = resumeWaiters;
      resumeWaiters = [];
      waiters.forEach((resolve) => resolve());
    },
  };
};
